// src/pages/Sales/index.tsx

import React, { useState, useEffect } from 'react';
import {
    Container,
    Typography,
    Box,
    Tab,
    Tabs,
    Paper,
    CircularProgress,
    Alert,
    useMediaQuery,
    useTheme
} from '@mui/material';
import CalculateIcon from '@mui/icons-material/Calculate';
import BarChartIcon from '@mui/icons-material/BarChart';
import ListAltIcon from '@mui/icons-material/ListAlt';
import PeopleIcon from '@mui/icons-material/People';
import NotificationImportantIcon from '@mui/icons-material/NotificationImportant';
import { SalesList } from '../../components/Sales/SalesList';
import { SalesCalculation } from '../../components/Sales/SalesCalculation';
import { SalesReport } from '../../components/Sales/SalesReport';
import { SalesFilter } from '../../components/Sales/SalesFilter';
import { SalesStatusDialog } from '../../components/Sales/SalesStatusDialog';
import { SupervisorAllowanceList } from '../../components/Sales/SupervisorAllowanceList';
import { AttentionList } from '../../components/Sales/AttentionList';
import { salesService } from '../../services/salesService';
import {
    SalesResult,
    SalesSearchParams,
    SalesStatus,
    SalesCalculateResponse,
    SupervisorAllowance,
    SupervisorAllowanceSummary
} from '../../types/sales';
import { useUser } from '../../hooks/useUser';
import { useAuth } from 'react-oidc-context';

// 先月の年月を取得する関数
const getPreviousMonth = (): string => {
    const today = new Date();
    // 前月を取得（0が1月なので、今月の値-1が先月）
    const prevMonth = today.getMonth() === 0 ? 11 : today.getMonth() - 1;
    // 先月が12月なら年も1つ戻す
    const year = prevMonth === 11 && today.getMonth() === 0 ? today.getFullYear() - 1 : today.getFullYear();
    // YYYY-MM形式で返却（月は1始まりに調整するため+1）
    return `${year}-${String(prevMonth + 1).padStart(2, '0')}`;
};

const SalesPage: React.FC = () => {
    const { userInfo } = useUser();
    const auth = useAuth();
    const theme = useTheme();
    const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));

    // タブの状態
    const [activeTab, setActiveTab] = useState(0);

    // 売上データ関連の状態
    const [salesData, setSalesData] = useState<SalesResult[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    // 職長手当データ関連の状態
    const [supervisorAllowances, setSupervisorAllowances] = useState<SupervisorAllowance[]>([]);
    const [supervisorSummaries, setSupervisorSummaries] = useState<SupervisorAllowanceSummary[]>([]);
    const [allowanceLoading, setAllowanceLoading] = useState(false);
    const [allowanceError, setAllowanceError] = useState<string | null>(null);

    // デフォルトのフィルター - 売上計上月を先月に設定
    const [filters, setFilters] = useState<SalesSearchParams>({
        salesMonth: getPreviousMonth()
    });

    // 集計日関連の状態
    const [calculationDate, setCalculationDate] = useState<string | null>(null);
    const [calculationDateLoading, setCalculationDateLoading] = useState(false);

    // ステータス変更ダイアログの状態
    const [statusDialogOpen, setStatusDialogOpen] = useState(false);
    const [selectedSales, setSelectedSales] = useState<{
        id: number;
        currentStatus: SalesStatus;
        newStatus: SalesStatus;
    } | null>(null);

    // 初期ロード時に売上データを取得
    useEffect(() => {
        fetchSalesData();
    }, []);

    // フィルターが変更されたときに売上データを再取得
    useEffect(() => {
        fetchSalesData();
        if (activeTab === 1) { // 職長手当タブがアクティブの場合
            fetchSupervisorAllowanceData();
        }
    }, [filters]);

    // フィルターが変更されたときに集計日データを取得
    useEffect(() => {
        fetchCalculationDate();
    }, [filters]);

    // タブが変更されたときに必要に応じて職長手当データを取得
    useEffect(() => {
        if (activeTab === 1 && supervisorAllowances.length === 0) {
            fetchSupervisorAllowanceData();
        }
    }, [activeTab]);

    // 指定月の売上データを取得
    const fetchSalesData = async () => {
        setLoading(true);
        setError(null);

        try {
            // auth.user から access_token を取得
            const token = auth.user?.access_token;

            // 選択月のデータを取得
            const data = await salesService.getAll(filters, token);
            console.log('取得した売上データ:', data);
            setSalesData(data);
        } catch (err: any) {
            console.error('売上データの取得に失敗しました:', err);
            setError(err.message || '売上データの取得に失敗しました');
            setSalesData([]);
        } finally {
            setLoading(false);
        }
    };

    // 職長手当データの取得
    const fetchSupervisorAllowanceData = async () => {
        setAllowanceLoading(true);
        setAllowanceError(null);

        try {
            const token = auth.user?.access_token;
            const yearMonth = filters.salesMonth || getPreviousMonth();

            // 職長手当詳細データの取得
            const allowances = await salesService.getSupervisorAllowancesByMonth(yearMonth, token);
            setSupervisorAllowances(allowances);

            // 職長手当集計データの取得
            const summaries = await salesService.getSupervisorAllowanceSummaryByMonth(yearMonth, token);
            setSupervisorSummaries(summaries);
        } catch (err: any) {
            console.error('職長手当データの取得に失敗しました:', err);
            setAllowanceError(err.message || '職長手当データの取得に失敗しました');
            setSupervisorAllowances([]);
            setSupervisorSummaries([]);
        } finally {
            setAllowanceLoading(false);
        }
    };

    // タブ変更時の処理
    const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
        setActiveTab(newValue);
    };

    // フィルター変更時の処理 - 売上計上月のみを受け取る
    const handleFilterChange = (salesMonth: string) => {
        setFilters({ salesMonth });
        setLoading(true); // フィルター変更時にローディング状態にする
        if (activeTab === 1) {
            setAllowanceLoading(true);
        }
    };

    // 売上集計完了時の処理
    const handleCalculationComplete = (result: SalesCalculateResponse) => {
        // 集計結果を反映するために売上データを再取得
        fetchSalesData();
        // 職長手当データも更新
        fetchSupervisorAllowanceData();
    };

    // ステータス変更ボタンクリック時の処理
    const handleStatusChange = (id: number, newStatus: SalesStatus) => {
        const salesItem = salesData.find(item => item.id === id);
        if (!salesItem) return;

        setSelectedSales({
            id,
            currentStatus: salesItem.status,
            newStatus
        });

        setStatusDialogOpen(true);
    };

    // ステータス変更成功時の処理
    const handleStatusUpdateSuccess = () => {
        setStatusDialogOpen(false);
        fetchSalesData();
    };

    // 経理担当者かどうかをチェック
    const isClerk = (): boolean => {
        // user_typeプロパティを使って判定
        return userInfo?.user_type === 'clerk';
    };


    // 集計日データの取得
    const fetchCalculationDate = async () => {
        setCalculationDateLoading(true);

        try {
            const token = auth.user?.access_token;
            const yearMonth = filters.salesMonth || getPreviousMonth();

            // 集計日の取得
            const dateData = await salesService.getCalculationDate(yearMonth, token);
            setCalculationDate(dateData);
        } catch (err: any) {
            console.error('集計日の取得に失敗しました:', err);
            setCalculationDate(null);
        } finally {
            setCalculationDateLoading(false);
        }
    };



    // タブの内容をレンダリングする関数
    const renderTabContent = () => {
        switch (activeTab) {
            case 0: // 売上一覧
                return (
                    <>
                        <SalesFilter
                            currentMonth={filters.salesMonth || getPreviousMonth()}
                            onMonthChange={handleFilterChange}
                            calculationDate={calculationDate}
                            calculationDateLoading={calculationDateLoading}
                        />

                        {loading ? (
                            <Box display="flex" justifyContent="center" p={3}>
                                <CircularProgress />
                            </Box>
                        ) : error ? (
                            <Alert severity="error" sx={{ mb: 3 }}>
                                {error}
                            </Alert>
                        ) : (
                            <SalesList
                                salesData={salesData}
                                onStatusChange={handleStatusChange}
                                loading={loading}
                            />
                        )}
                    </>
                );
            case 1: // 職長手当
                return (
                    <>
                        <SalesFilter
                            currentMonth={filters.salesMonth || getPreviousMonth()}
                            onMonthChange={handleFilterChange}
                            calculationDate={calculationDate}
                            calculationDateLoading={calculationDateLoading}
                        />

                        <SupervisorAllowanceList
                            month={filters.salesMonth || getPreviousMonth()}
                            allowances={supervisorAllowances}
                            summaries={supervisorSummaries}
                            loading={allowanceLoading}
                            error={allowanceError}
                            salesData={salesData} // 請求月グループ化のために売上データを渡す
                        />
                    </>
                );
            case 2: // 売上集計（経理担当者のみ）
                if (!isClerk()) return null;
                return (
                    <SalesCalculation
                        onCalculationComplete={handleCalculationComplete}
                    />
                );
            case 3: // レポート
                return <SalesReport />;
            case 4: // 要対応（経理担当者のみ）
                if (!isClerk()) return null;
                return <AttentionList />;
            default:
                return null;
        }
    };

    return (
        <Container maxWidth="lg" sx={{ py: 3 }}>
            <Typography variant="h4" component="h1" gutterBottom sx={{ mt: { xs: 1, sm: 3 } }}>
                売上管理
            </Typography>

            <Paper sx={{ mb: 3 }}>
                <Tabs
                    value={activeTab}
                    onChange={handleTabChange}
                    variant={isSmallScreen ? "scrollable" : "fullWidth"}
                    scrollButtons={isSmallScreen ? "auto" : false}
                    indicatorColor="primary"
                    textColor="primary"
                >
                    <Tab
                        icon={<ListAltIcon />}
                        label="売上一覧"
                        iconPosition="start"
                    />
                    <Tab
                        icon={<PeopleIcon />}
                        label="職長手当"
                        iconPosition="start"
                    />
                    {isClerk() && (
                        <Tab
                            icon={<CalculateIcon />}
                            label="売上集計"
                            iconPosition="start"
                        />
                    )}
                    <Tab
                        icon={<BarChartIcon />}
                        label="レポート"
                        iconPosition="start"
                    />
                    {isClerk() && (
                        <Tab
                            icon={<NotificationImportantIcon />}
                            label="要対応"
                            iconPosition="start"
                        />
                    )}
                </Tabs>
            </Paper>

            {renderTabContent()}

            {/* ステータス変更ダイアログ */}
            {selectedSales && (
                <SalesStatusDialog
                    open={statusDialogOpen}
                    onClose={() => setStatusDialogOpen(false)}
                    salesId={selectedSales.id}
                    currentStatus={selectedSales.currentStatus}
                    newStatus={selectedSales.newStatus}
                    onSuccess={handleStatusUpdateSuccess}
                />
            )}
        </Container>
    );
};

export default SalesPage;